import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { School } from '../../types/admin';
import { School as SchoolIcon, Plus, X } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import { useSchool } from '../../contexts/SchoolContext';
import toast from 'react-hot-toast';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export default function SchoolSettingsPage() {
  useDocumentTitle('School Settings');
  
  const { selectedSchool, setSelectedSchool } = useSchool();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<School> | null>(null);
  const [newTerm, setNewTerm] = useState({
    name: '',
    startDate: '',
    endDate: ''
  });
  const [newDepartment, setNewDepartment] = useState('');

  useEffect(() => {
    if (selectedSchool) {
      setFormData(selectedSchool);
      setLoading(false);
    }
  }, [selectedSchool]);

  if (!formData || loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Loading school settings...</div>
        </div>
      </AppLayout>
    );
  }

  const handleSave = async () => {
    if (!selectedSchool || !formData) return;

    try {
      setSaving(true);
      
      // Update school document
      await updateDoc(doc(db, 'schools', selectedSchool.id), {
        ...formData,
        updatedAt: new Date().toISOString()
      });

      // Update context
      await setSelectedSchool({
        ...selectedSchool,
        ...formData,
        updatedAt: new Date().toISOString()
      });

      toast.success('School settings updated successfully');
    } catch (error) {
      console.error('Error updating school settings:', error);
      toast.error('Failed to update school settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTerm = () => {
    if (!formData || !newTerm.name || !newTerm.startDate || !newTerm.endDate) return;

    const term = {
      id: crypto.randomUUID(),
      name: newTerm.name,
      startDate: newTerm.startDate,
      endDate: newTerm.endDate
    };

    setFormData({
      ...formData,
      settings: {
        ...formData.settings,
        terms: [...(formData.settings?.terms || []), term]
      }
    });

    setNewTerm({ name: '', startDate: '', endDate: '' });
  };

  const handleRemoveTerm = (termId: string) => {
    if (!formData) return;

    setFormData({
      ...formData,
      settings: {
        ...formData.settings,
        terms: formData.settings?.terms.filter(term => term.id !== termId) || []
      }
    });
  };

  const handleAddDepartment = () => {
    if (!formData || !newDepartment.trim()) return;

    setFormData({
      ...formData,
      settings: {
        ...formData.settings,
        departments: [...(formData.settings?.departments || []), newDepartment.trim()]
      }
    });

    setNewDepartment('');
  };

  const handleRemoveDepartment = (department: string) => {
    if (!formData) return;

    setFormData({
      ...formData,
      settings: {
        ...formData.settings,
        departments: formData.settings?.departments.filter(d => d !== department) || []
      }
    });
  };

  return (
    <AppLayout>
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <SchoolIcon className="w-8 h-8 text-primary-500" />
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">School Settings</h1>
              <p className="text-text-secondary">Manage your school's information and configuration</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-surface-light rounded-lg p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">Basic Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    School Name
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                             focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    School Code
                  </label>
                  <input
                    type="text"
                    value={formData.code || ''}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                             focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    placeholder="e.g., ABC123"
                  />
                  <p className="text-xs text-text-secondary mt-1">
                    This code is used for staff invitations and joining the school
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    School Type
                  </label>
                  <select
                    value={formData.type || 'public'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as School['type'] })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                             focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  >
                    <option value="public">Public School</option>
                    <option value="private">Private School</option>
                    <option value="charter">Charter School</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Grade Range - Start
                    </label>
                    <select
                      value={formData.gradeRange?.start || 'K'}
                      onChange={(e) => setFormData({
                        ...formData,
                        gradeRange: { ...formData.gradeRange, start: e.target.value }
                      })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    >
                      {['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map(grade => (
                        <option key={grade} value={grade}>{grade}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Grade Range - End
                    </label>
                    <select
                      value={formData.gradeRange?.end || '12'}
                      onChange={(e) => setFormData({
                        ...formData,
                        gradeRange: { ...formData.gradeRange, end: e.target.value }
                      })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    >
                      {['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map(grade => (
                        <option key={grade} value={grade}>{grade}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-surface-light rounded-lg p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">Contact Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData.address || ''}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                             focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    placeholder="Street address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={formData.city || ''}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      value={formData.state || ''}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      placeholder="State"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      value={formData.zipCode || ''}
                      onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      placeholder="ZIP code"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      placeholder="Phone number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website || ''}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                             focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    placeholder="https://www.school.edu"
                  />
                </div>
              </div>
            </div>

            {/* Academic Terms */}
            <div className="bg-surface-light rounded-lg p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">Academic Terms</h2>
              
              <div className="space-y-4">
                {/* Add New Term */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={newTerm.name}
                      onChange={(e) => setNewTerm({ ...newTerm, name: e.target.value })}
                      placeholder="Term name"
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="date"
                      value={newTerm.startDate}
                      onChange={(e) => setNewTerm({ ...newTerm, startDate: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="date"
                      value={newTerm.endDate}
                      onChange={(e) => setNewTerm({ ...newTerm, endDate: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    />
                  </div>
                  <button
                    onClick={handleAddTerm}
                    disabled={!newTerm.name || !newTerm.startDate || !newTerm.endDate}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                             transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                {/* Term List */}
                <div className="space-y-2">
                  {formData.settings?.terms.map((term) => (
                    <div
                      key={term.id}
                      className="flex items-center justify-between p-4 bg-background rounded-lg"
                    >
                      <div>
                        <h3 className="font-medium text-text-primary">{term.name}</h3>
                        <p className="text-sm text-text-secondary">
                          {new Date(term.startDate).toLocaleDateString()} - {new Date(term.endDate).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveTerm(term.id)}
                        className="p-1 text-text-secondary hover:text-error-DEFAULT transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Departments */}
            <div className="bg-surface-light rounded-lg p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">Departments</h2>
              
              <div className="space-y-4">
                {/* Add New Department */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newDepartment}
                    onChange={(e) => setNewDepartment(e.target.value)}
                    placeholder="Department name"
                    className="flex-1 px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                             focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  />
                  <button
                    onClick={handleAddDepartment}
                    disabled={!newDepartment.trim()}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                             transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                {/* Department List */}
                <div className="flex flex-wrap gap-2">
                  {formData.settings?.departments.map((department) => (
                    <div
                      key={department}
                      className="flex items-center gap-2 px-3 py-1.5 bg-background rounded-full"
                    >
                      <span className="text-sm text-text-primary">{department}</span>
                      <button
                        onClick={() => handleRemoveDepartment(department)}
                        className="p-0.5 text-text-secondary hover:text-error-DEFAULT transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}